// Rug Room Shots: the two speeds (SPEC 9.11) and the half-price batch status pieces
// shared by the New job, Jobs and Review tabs and the compare view.
//   fast  = "Fast", real-time generateContent, $0.151 per 4K image, results in minutes
//   batch = "Half price", Gemini Batch API, $0.076 per 4K image, usually within hours, up to 24 h
// The backend's GET /config "speeds" is the source of truth (prices and availability);
// the fallbacks below only keep the page usable while /config loads or on an older backend,
// and they never offer Half price unless the backend says it is available.
import React from 'react';
import {
  Box, Chip, Stack, Typography, Radio, RadioGroup, FormControlLabel, Tooltip, Paper, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, CircularProgress,
} from '@mui/material';
import { MdBolt, MdSavings, MdSchedule } from 'react-icons/md';
import {
  AI_COLOR, GEN_STATUS, fmtRate, fmtUsd, fmtInt, fmtNy, fmtNyFull, fmtAgo, fmtDuration, fmtElapsed, tsMs,
  errorWords, DEFAULT_CONFIG,
} from './common';

export const SPEED_FAST = 'fast';
export const SPEED_BATCH = 'batch';
export const OVERDUE_MS = 24 * 3600 * 1000; // Google's target turnaround (SPEC 9.7)

const SPEED_TEXT = {
  fast: { label: 'Fast', note: 'results in minutes' },
  batch: { label: 'Half price', note: "usually within hours, up to 24 h (Google's target)" },
};

// Reason codes from the backend's 409 batch_unavailable / config (RrsSpeedPolicy).
const UNAVAILABLE_TEXT = {
  not_enabled: 'Half price is not switched on yet.',
  worker_offline: 'Half price needs the worker, and the worker is offline.',
  worker_not_ready: 'The worker has not been updated for Half price yet.',
  worker_batch_off: 'Half price is switched off on the worker.',
};

export const speedLabel = (code) => (SPEED_TEXT[code] || {}).label || (code ? String(code) : SPEED_TEXT.fast.label);
export const isBatch = (code) => code === SPEED_BATCH;

export const unavailableText = (reason, message) => {
  if (message) return message;
  return UNAVAILABLE_TEXT[reason] || 'Half price is not switched on yet.';
};

// The words for a 409 { code: 'batch_unavailable', error, current: { reason } } from Start / Regenerate.
export const batchUnavailableFromError = (e) => {
  const body = (e && e.body) || {};
  const reason = (body.current && body.current.reason) || body.reason || null;
  return unavailableText(reason, body.error || body.message);
};

// Normalised list [{ code, label, usdPerImage, note, available, reason }] from /config.
// GET /health (every 30 s) carries the same availability check (batchAvailable +
// batchUnavailableReason) and is fresher than /config, so it wins when present.
export const speedsFromConfig = (config, health) => {
  const c = config || DEFAULT_CONFIG;
  const fromApi = Array.isArray(c.speeds) ? c.speeds : [];
  const byCode = {};
  fromApi.forEach((s) => { if (s && s.code) byCode[String(s.code).toLowerCase()] = s; });
  const fast = byCode.fast || {};
  const batch = byCode.batch || {};
  const healthSays = health && typeof health.batchAvailable === 'boolean' ? health.batchAvailable : null;
  const workerOffline = !!(health && health.worker && health.worker.online === false);
  const batchAvailable = healthSays !== null ? healthSays : (batch.available === true && !workerOffline);
  let batchReason = '';
  if (!batchAvailable) {
    const configReason = batch.unavailableReason || batch.reason || null;
    const healthReason = healthSays === false ? (health.batchUnavailableReason || null) : null;
    if (healthReason && healthReason !== configReason) batchReason = unavailableText(healthReason);
    else if (!healthReason && batch.available === true && workerOffline) batchReason = UNAVAILABLE_TEXT.worker_offline;
    else batchReason = unavailableText(configReason || healthReason, batch.unavailableMessage || batch.message);
  }
  return [
    {
      code: SPEED_FAST,
      label: fast.label || SPEED_TEXT.fast.label,
      usdPerImage: Number(fast.usdPerImage ?? c.usdPerImage ?? DEFAULT_CONFIG.usdPerImage),
      note: SPEED_TEXT.fast.note,
      available: fast.available !== false,
      reason: '',
    },
    {
      code: SPEED_BATCH,
      label: batch.label || SPEED_TEXT.batch.label,
      usdPerImage: Number(batch.usdPerImage ?? c.usdPerImageBatch ?? DEFAULT_CONFIG.usdPerImageBatch),
      note: SPEED_TEXT.batch.note,
      available: batchAvailable,
      reason: batchReason,
    },
  ];
};

// Per-image price at a speed. A job's own rate snapshot wins over the config.
export const rateFor = (speed, speeds, job) => {
  if (isBatch(speed)) {
    if (job && job.usdPerImageBatch !== undefined && job.usdPerImageBatch !== null) return Number(job.usdPerImageBatch);
  } else if (job && job.usdPerImage !== undefined && job.usdPerImage !== null) return Number(job.usdPerImage);
  const s = (speeds || []).find((x) => x.code === speed);
  if (s) return s.usdPerImage;
  return isBatch(speed) ? DEFAULT_CONFIG.usdPerImageBatch : DEFAULT_CONFIG.usdPerImage;
};

// The amount the backend recomputes for its stale-plan check (Money() rounds to 4 decimals).
export const estimateFor = (count, rate) => Math.round((Number(count) || 0) * (Number(rate) || 0) * 10000) / 10000;

// The default speed for a new Start / Regenerate: the job's speed when it is still available.
export const defaultSpeed = (preferred, speeds) => {
  const want = isBatch(preferred) ? SPEED_BATCH : SPEED_FAST;
  const s = (speeds || []).find((x) => x.code === want);
  return s && s.available ? want : SPEED_FAST;
};

// ---------------------------------------------------------------------------
// Speed chooser: two radio options with the price and the turnaround; Half price is
// disabled (with the reason) until the backend makes it available. `count` adds the
// total at each speed ("183 images: $27.63").
// ---------------------------------------------------------------------------
export const SpeedChooser = ({ value, onChange, speeds, count, job, disabled, dense, row }) => {
  const list = speeds && speeds.length ? speeds : speedsFromConfig(DEFAULT_CONFIG);
  return (
    <RadioGroup
      row={!!row}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ gap: row ? 2 : 0.25 }}
    >
      {list.map((s) => {
        const rate = rateFor(s.code, list, job);
        const total = count !== undefined && count !== null ? estimateFor(count, rate) : null;
        const Icon = isBatch(s.code) ? MdSavings : MdBolt;
        const label = (
          <Box sx={{ py: dense ? 0 : 0.25 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Icon size={16} style={{ opacity: 0.8 }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.label}</Typography>
              <Typography variant="body2" color="text.secondary">· {fmtRate(rate)} per image</Typography>
              {total !== null && (
                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  · {fmtUsd(total)}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              {s.available ? s.note : s.reason}
            </Typography>
          </Box>
        );
        const ctl = (
          <FormControlLabel
            key={s.code}
            value={s.code}
            disabled={disabled || !s.available}
            control={<Radio size="small" />}
            label={label}
            sx={{ alignItems: 'flex-start', mr: 0, '& .MuiRadio-root': { pt: dense ? 0.25 : 0.75 } }}
          />
        );
        return !s.available && s.reason
          ? <Tooltip key={s.code} title={s.reason} placement="top-start"><span>{ctl}</span></Tooltip>
          : ctl;
      })}
    </RadioGroup>
  );
};

export const SpeedChip = ({ speed, size = 'small', sx }) => {
  const batch = isBatch(speed);
  return (
    <Chip
      size={size}
      icon={batch ? <MdSavings /> : <MdBolt />}
      label={speedLabel(speed)}
      variant="outlined"
      color={batch ? 'secondary' : 'default'}
      sx={{ '& .MuiChip-icon': { fontSize: 14 }, ...sx }}
    />
  );
};

// "183 images · Fast $27.63 · Half price $13.91"
export const bothTotalsText = (count, speeds, job) => {
  const n = Number(count) || 0;
  const parts = (speeds || []).map((s) => `${s.label} ${fmtUsd(estimateFor(n, rateFor(s.code, speeds, job)))}`);
  return `${fmtInt(n)} image${n === 1 ? '' : 's'} · ${parts.join(' · ')}`;
};

// ---------------------------------------------------------------------------
// Batch states (rrs_batches.status + Google's normalised state).
// ---------------------------------------------------------------------------
const GOOGLE_STATE = {
  PENDING: { label: 'Waiting', color: 'default' },
  RUNNING: { label: 'Running', color: 'info' },
  SUCCEEDED: { label: 'Done', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' },
  CANCELLED: { label: 'Cancelled', color: 'default' },
  EXPIRED: { label: 'Expired', color: 'warning' },
};

const BATCH_STATUS = {
  building: { label: 'Preparing', color: 'default' },
  uploading: { label: 'Uploading', color: 'default' },
  ready: { label: 'Sending', color: 'default' },
  submitting: { label: 'Sending', color: 'default' },
  create_unknown: { label: 'Checking with Google', color: 'warning' },
  submitted: { label: 'Waiting', color: 'default' },
  collecting: { label: 'Collecting', color: 'info' },
  done: { label: 'Done', color: 'success' },
  create_failed: { label: 'Refused', color: 'error' },
  cancelled_unsent: { label: 'Cancelled (not sent)', color: 'default' },
  unknown: { label: 'Unknown', color: 'warning' },
};

const normGoogle = (s) => (s ? String(s).toUpperCase().replace(/^(BATCH_STATE_|JOB_STATE_)/, '') : '');

export const batchStateInfo = (b) => {
  if (!b) return { label: '-', color: 'default' };
  const status = b.status || '';
  const g = normGoogle(b.googleState);
  // While Google holds it, Google's own state is the most useful word.
  if ((status === 'submitted' || status === 'collecting' || status === 'done') && GOOGLE_STATE[g]) {
    if (status === 'collecting' && g === 'SUCCEEDED') return BATCH_STATUS.collecting;
    return GOOGLE_STATE[g];
  }
  return BATCH_STATUS[status] || GOOGLE_STATE[g] || { label: status.replace(/_/g, ' ') || '-', color: 'default' };
};

const BATCH_ERRORS = {
  create_rejected: 'Google refused the batch (nothing billed).',
  create_rate_limited: 'Google is rate-limiting new batches; the worker retries automatically.',
  create_unknown: 'Google did not confirm the batch; the worker is checking whether it exists.',
  batch_failed: 'Google reported the batch as failed.',
  batch_expired: 'Google gave up on this batch after 48 h.',
  collect_failed: 'The results could not be downloaded yet; the worker keeps retrying.',
  count_mismatch: 'Google returned a different number of results than were sent.',
  batch_missing: 'Google no longer finds this batch; the worker keeps checking.',
  internal: 'Worker error.',
};

export const batchErrorText = (b) => {
  if (!b || (!b.errorCode && !b.errorMessage)) return '';
  const base = BATCH_ERRORS[b.errorCode] || (b.errorCode ? String(b.errorCode).replace(/_/g, ' ') : '');
  return [base, b.errorMessage].filter(Boolean).join(' ');
};

export const BatchStateChip = ({ batch, size = 'small' }) => {
  const info = batchStateInfo(batch);
  return <Chip size={size} label={info.label} color={info.color} variant={info.color === 'default' ? 'outlined' : 'filled'} />;
};

// "Half price: waiting on Google · sent 2 h 14 m ago · usually within hours, up to 24 h"
// for a generation row in batched / submitted (or a queued half-price row).
export const batchWaitInfo = (item, now) => {
  const batch = item.batch || null;
  if (item.status === 'queued') {
    return { title: 'Half price: waiting for the next batch', sub: 'The worker groups half-price images into a batch within about a minute.', overdue: false };
  }
  if (item.status === 'batched') {
    return { title: 'Half price: preparing the batch', sub: 'Uploading the images to Google.', overdue: false };
  }
  const sentTs = (batch && batch.submittedAt) || item.sentAt;
  const sentMs = tsMs(sentTs);
  const waited = sentMs ? now - sentMs : null;
  const overdue = (batch && batch.overdue === true) || (waited !== null && waited > OVERDUE_MS);
  const state = batch && (batch.status || batch.googleState) ? batchStateInfo(batch).label : null;
  return {
    title: 'Half price: waiting on Google',
    sub: [
      sentMs ? `sent ${fmtDuration(waited)} ago` : null,
      state && state !== 'Waiting' && state !== '-' ? `Google: ${state.toLowerCase()}` : null,
      overdue ? 'over Google\'s 24 h target (Google gives up at 48 h)' : 'usually within hours, up to 24 h',
    ].filter(Boolean).join(' · '),
    overdue,
  };
};

// ---------------------------------------------------------------------------
// "Batches" section (Review and plan header): one row per Google batch of the job.
// ---------------------------------------------------------------------------
export const BatchesSection = ({ batches, now, title = 'Half-price batches' }) => {
  const list = Array.isArray(batches) ? batches : [];
  if (!list.length) return null;
  const t = now || Date.now();
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <MdSchedule size={18} style={{ opacity: 0.75 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          Google&apos;s Batch API at half price: usually within hours, up to 24 h. Checked about every minute.
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Batch</TableCell>
              <TableCell align="right">Images</TableCell>
              <TableCell>Google</TableCell>
              <TableCell>Sent (NY)</TableCell>
              <TableCell>Last checked</TableCell>
              <TableCell align="right">Back</TableCell>
              <TableCell>Problem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((b) => {
              const sentMs = tsMs(b.submittedAt);
              const ended = !!(b.finishedAt || b.terminalAt);
              const overdue = b.overdue === true || (!ended && sentMs && t - sentMs > OVERDUE_MS);
              const back = b.linesCollected !== undefined && b.linesCollected !== null
                ? b.linesCollected
                : (b.succeededCount || 0) + (b.failedCount || 0);
              const err = batchErrorText(b);
              return (
                <TableRow key={b.batchId}>
                  <TableCell>
                    <Tooltip title={[b.displayName, b.googleName].filter(Boolean).join(' · ') || ''}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>#{b.batchId}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{fmtInt(b.lineCount || 0)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <BatchStateChip batch={b} />
                      {b.cancelRequestedAt && !ended ? <Chip size="small" variant="outlined" color="warning" label="cancelling" /> : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {b.submittedAt ? (
                      <Tooltip title={`${fmtNyFull(b.submittedAt)} (New York)`}>
                        <Typography variant="body2" component="span" color={overdue ? 'warning.main' : undefined}>
                          {fmtNy(b.submittedAt)} · {fmtAgo(b.submittedAt, t)}
                        </Typography>
                      </Tooltip>
                    ) : <Typography variant="body2" color="text.disabled" component="span">not yet</Typography>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {b.lastPolledAt ? <Typography variant="body2" component="span">{fmtAgo(b.lastPolledAt, t)}</Typography> : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtInt(back)} of {fmtInt(b.lineCount || 0)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    {overdue && !err ? (
                      <Typography variant="caption" color="warning.main">
                        Over Google&apos;s 24 h target; Google gives up at 48 h.
                      </Typography>
                    ) : null}
                    {err ? <Typography variant="caption" color={b.errorCode === 'create_rate_limited' ? 'text.secondary' : 'error.main'}>{err}</Typography> : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// What the result panel shows while there is no image yet (Review row and compare
// view): a spinner for Fast work, a clock for half-price work, the error in words
// for failed / unknown rows. Returns { content, color } so the caller frames it.
// ---------------------------------------------------------------------------
export const resultStatusContent = (item, now, large) => {
  const big = large ? 44 : 34;
  const batchRow = isBatch(item.speed) || item.status === 'batched' || item.status === 'submitted';
  if (batchRow && ['queued', 'batched', 'submitted'].includes(item.status)) {
    const w = batchWaitInfo(item, now);
    return {
      color: w.overdue ? 'warning.main' : undefined,
      content: (
        <>
          <MdSchedule size={big} style={{ marginBottom: 10, opacity: 0.8 }} />
          <Typography variant={large ? 'subtitle1' : 'body2'} sx={{ fontWeight: 700 }}>{w.title}</Typography>
          <Typography variant={large ? 'body2' : 'caption'} sx={{ opacity: 0.85, mt: 0.5 }}>{w.sub}</Typography>
        </>
      ),
    };
  }
  if (item.status === 'queued' || item.status === 'running') {
    const since = tsMs(item.status === 'running' ? (item.claimedAt || item.requestedAt) : item.requestedAt);
    return {
      content: (
        <>
          <CircularProgress size={big} sx={{ color: AI_COLOR, mb: 1.5 }} />
          <Typography variant={large ? 'subtitle1' : 'body2'} sx={{ fontWeight: 600 }}>{item.status === 'running' ? 'generating' : 'queued'}</Typography>
          {since ? <Typography variant="caption" color="text.secondary">{fmtElapsed(now - since)}</Typography> : null}
        </>
      ),
    };
  }
  if (item.status === 'succeeded') {
    return {
      content: (
        <>
          <CircularProgress size={large ? 34 : 28} sx={{ mb: 1.5 }} />
          <Typography variant="body2">Saving the image...</Typography>
        </>
      ),
    };
  }
  if (item.status === 'failed' || item.status === 'unknown') {
    const fallback = item.status === 'unknown' ? 'Timed out after sending: may have been billed.' : 'No image.';
    // Batch error codes have better words here than the backend's generic text.
    const words = (item.errorCode && String(item.errorCode).startsWith('batch_'))
      ? errorWords(item.errorCode, item.errorMessage)
      : (item.errorText || errorWords(item.errorCode, item.errorMessage));
    return {
      color: item.status === 'failed' ? 'error.main' : 'warning.main',
      content: (
        <>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{item.status === 'failed' ? 'Failed' : 'Unknown outcome'}</Typography>
          <Typography variant="body2">{words || fallback}</Typography>
          {item.errorText && item.errorMessage && !String(item.errorCode || '').startsWith('batch_')
            ? <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.8 }}>{String(item.errorMessage).slice(0, 200)}</Typography> : null}
        </>
      ),
    };
  }
  return {
    content: <Typography variant="body2" color="text.secondary">{(GEN_STATUS[item.status] || {}).label || item.status}</Typography>,
  };
};

// Button word for a new attempt (SPEC 9.8): Regenerate, Retry (failed), Retry anyway (unknown).
export const regenWord = (status) => {
  if (status === 'unknown') return 'Retry anyway';
  if (status === 'failed') return 'Retry';
  return 'Regenerate';
};
